import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePublicStoreSettings } from '../../hooks/usePublicStoreSettings';

/**
 * Storefront hero — FW2026 layout.
 *
 * Desktop  → split panel: cream text column on the left (overline, two-line
 *            serif headline with italic accent + underline, body copy, primary
 *            black CTA "Comprar ahora", secondary text link "Ver colecciones")
 *            and full-bleed model image on the right.
 *            Bottom of the left column shows "01 / Summer essentials" with
 *            pagination dots — laid out as a static indicator. Wiring a real
 *            slideshow is a follow-up; the markup is ready for it (just swap
 *            `slides[0]` for an array + state).
 * Mobile   → full-bleed image with bottom text overlay. Same content, white
 *            type on a dark gradient so it stays readable against any image.
 *
 * Image strategy: serve WebP when supported, fall back to JPEG. Both URLs
 * come from `public/images/` so the HTML preload tag in index.html shares
 * the exact src — no double-download on first paint.
 */
// Desktop crop — tall composition that suits the 46% left-text / right-image split.
const HeroImg = process.env.PUBLIC_URL + '/images/hero-img.jpeg';
// Mobile crop — wider/lower composition that keeps the subject from being
// cut in half by the overlay copy. Falls back to the desktop image if the
// mobile asset isn't deployed yet (browser silently skips the missing
// <source> in <picture>, then we hand it the desktop JPEG).
const HeroImgMobile = process.env.PUBLIC_URL + '/images/hero-img-mobile.jpeg';

// Static for now — keep this as data so swapping in real rotation later is
// just `useState(0)` + the same render code.
const SLIDES = [
  { idx: '01', label: 'Summer essentials' },
  // Add { idx: '02', label: '…' } here when more slides exist.
];

const HeroSection = () => {
  const navigate = useNavigate();
  const slideIndex = 0;
  const slide = SLIDES[slideIndex];

  // Owner-uploaded hero (Configuración → "Inicio — foto principal",
  // 2026-08). Empty setting = the bundled photos above, so clearing the
  // field always restores the original. A custom photo is one file used
  // for both crops — object-fit/positioning below keeps it presentable.
  const { data: storeSettings } = usePublicStoreSettings();
  const customHero = (storeSettings?.['home.hero_image_url'] || '').trim();
  const heroSrc = customHero || HeroImg;
  const heroSrcMobile = customHero || HeroImgMobile;
  // Season tag above the headline — owner-editable (Configuración →
  // "Inicio — etiqueta de temporada"). Empty setting = the fallback.
  const heroEyebrow = (storeSettings?.['home.hero_eyebrow'] || '').trim() || 'SS 2027';

  // SS pass (2026-08): the headline accent uses the brand `accent-strong`
  // token — rosier than the old FW brown (#A56A5C) and still ≥3:1 on the
  // cream background.
  const accent = '#B8746B';

  return (
    <section aria-label="Hero" className="relative w-full bg-bg-base overflow-hidden">
      {/* ── MOBILE — full-bleed image w/ overlay text.
          The mobile photo is a wide composition (≈16:9) with the model
          at ~75% from left. Using `aspect-ratio: 3/4` + `object-position`
          anchored to the right keeps the model in frame while still
          leaving the darker left wall area for the text overlay. */}
      <div className="lg:hidden relative w-full" style={{ aspectRatio: '3/4', minHeight: '420px' }}>
        <picture>
          {/* Source order matters — browser picks the first supported
              type. AVIF (newest, smallest) → WebP → JPEG fallback.
              Mobile crops first; desktop crops at the bottom guarantee
              we always render something even when the mobile asset
              hasn't been deployed yet. */}
          <source type="image/jpeg" srcSet={heroSrcMobile} />
          <img
            src={heroSrc}
            alt="Pinksisly — Nueva colección"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: '78% center' }}
          />
        </picture>

        {/* Left-anchored gradient: darker on the left where the copy sits,
            fading out around the model so the photograph still reads.
            Adjusted from a bottom gradient — with a wide image cropped to
            portrait the bottom darkening was washing the subject out. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(to right, rgba(20,15,12,0.72) 0%, rgba(20,15,12,0.4) 38%, rgba(20,15,12,0.08) 62%, transparent 85%)',
          }}
        />

        <div className="absolute inset-x-0 bottom-0 px-6 pb-9">
          <p
            className="text-[10px] tracking-[0.28em] uppercase text-white/70 mb-3"
            aria-hidden="true"
          >
            {heroEyebrow}
          </p>
          <h1 className="font-display tracking-tight mb-4">
            <span className="block text-[46px] leading-[0.95] text-white">El nuevo</span>
            <span
              className="inline-block italic text-[46px] leading-[0.95]"
              style={{ color: '#E8B4A8', borderBottom: '1px solid #E8B4A8', paddingBottom: 6 }}
            >
              capítulo
            </span>
          </h1>
          <p className="text-[12.5px] leading-[1.55] text-white/85 mb-7 max-w-[280px]">
            Siluetas suaves, capas limpias y prendas pensadas para todos los días.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/ropas')}
              className="inline-flex items-center justify-center gap-3 h-11 bg-white text-ink-primary text-[11px] tracking-[0.22em] uppercase font-medium active:bg-accent-soft transition-colors duration-200"
              style={{ width: '210px' }}
            >
              Comprar ahora
            </button>
            <button
              onClick={() => navigate('/colecciones')}
              className="self-start text-[11px] tracking-[0.22em] uppercase text-white/95 underline-offset-[6px] underline decoration-white/60 pb-1"
            >
              Ver colecciones
            </button>
          </div>
        </div>
      </div>

      {/* ── DESKTOP — split: left text column · right image ── */}
      <div
        className="hidden lg:flex w-full relative"
        style={{ minHeight: 'clamp(600px, 86vh, 820px)' }}
      >
        {/* LEFT — text */}
        <div
          className="flex flex-col justify-between bg-bg-base z-10 px-12 xl:px-20 py-16"
          style={{ width: '46%', flexShrink: 0 }}
        >
          <div className="flex-1 flex items-center">
            <div className="max-w-[420px]">
              <p
                className="text-[11px] tracking-[0.28em] uppercase text-ink-tertiary mb-10"
                aria-hidden="true"
              >
                {heroEyebrow}
              </p>
              <h1 className="font-display tracking-tight mb-8">
                <span className="block text-[76px] xl:text-[88px] leading-[1.02] text-ink-primary">
                  El nuevo
                </span>
                <span
                  className="inline-block italic text-[76px] xl:text-[88px] leading-[1.02]"
                  style={{
                    color: accent,
                    borderBottom: `1.5px solid ${accent}`,
                    paddingBottom: 10,
                    marginTop: -4,
                  }}
                >
                  capítulo
                </span>
              </h1>
              <p className="text-[14px] leading-[1.7] text-ink-secondary mb-10 max-w-[340px]">
                Siluetas suaves, capas limpias y<br />
                prendas pensadas para todos los días.
              </p>
              <div className="flex items-center gap-7">
                <button
                  onClick={() => navigate('/ropas')}
                  className="inline-flex items-center justify-center bg-ink-primary text-white text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-accent-strong transition-colors duration-300"
                  style={{ height: '52px', padding: '0 36px', letterSpacing: '0.22em' }}
                >
                  Comprar ahora
                </button>
                <button
                  onClick={() => navigate('/colecciones')}
                  className="text-[11px] tracking-[0.24em] uppercase text-ink-primary underline-offset-[8px] underline decoration-ink-primary/60 pb-1 hover:decoration-ink-primary transition-colors"
                >
                  Ver colecciones
                </button>
              </div>
            </div>
          </div>

          {/* Slide indicator — bottom-left. Index + label + pagination dots.
              The dots are decorative for now; when the slideshow lands they
              become clickable. */}
          <div className="flex items-center gap-5 text-ink-tertiary">
            <span className="text-[12px] tracking-[0.12em] tabular-nums text-ink-primary">
              {slide.idx}
            </span>
            <span className="text-[11px] tracking-[0.18em] uppercase text-ink-tertiary">
              / {slide.label}
            </span>
            <span className="inline-flex items-center gap-[6px] ml-4">
              {/* Always render a fixed 4-dot strip so the indicator has a
                  consistent visual rhythm whether or not extra slides exist. */}
              {Array.from({ length: 4 }).map((_, i) => (
                <span
                  key={i}
                  aria-hidden="true"
                  style={{
                    width: i === slideIndex ? 8 : 4,
                    height: 4,
                    borderRadius: 999,
                    background: i === slideIndex ? '#1A1A1A' : 'rgba(26,26,26,0.22)',
                    transition: 'width 0.18s ease, background 0.18s ease',
                  }}
                />
              ))}
            </span>
          </div>
        </div>

        {/* Soft seam — fades the cream into the image so the panel split
            doesn't read as a hard rule. */}
        <div
          className="absolute top-0 bottom-0 z-10 pointer-events-none"
          style={{
            left: '46%',
            width: '120px',
            background:
              'linear-gradient(to right, var(--bg-base, #FAF6F1) 0%, rgba(250,246,241,0.5) 40%, transparent 100%)',
          }}
        />

        {/* RIGHT — image */}
        <div className="relative flex-1 overflow-hidden bg-bg-muted">
          <picture>
            <img
              src={heroSrc}
              alt="Pinksisly — Nueva colección"
              fetchPriority="high"
              loading="eager"
              decoding="async"
              className="w-full h-full object-cover object-top"
            />
          </picture>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
